import { FileActionButton } from "../file-action/file-action-controls";
import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { Dropdown, DropdownChangeEvent, DropdownFilterEvent } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { ComplaintSummaryRequest } from '../../services/order/order.service';
import { getComplaintStatus } from '../../services/complaint/ComplaintService';
import orderServices from '../../services/orders/orderservice';
import { userOption, userResult } from '../../models/orders/order-list-model';

interface ComplaintSummarySearchProps {
    onSearch: (filters: Partial<ComplaintSummaryRequest>) => void;
    setLoading: (loading: boolean) => void;
    onExport: () => void;
}

const ComplaintSummarySearch: React.FC<ComplaintSummarySearchProps> = ({ onSearch, setLoading, onExport }) => {
    const [filters, setFilters] = useState({
        complaintTypeId: null as number | null,
        statusId: null as string | null,
        priority: null as string | null,
        startDate: null as Date | null,
        endDate: null as Date | null,
    });

    const [orderUsers, setOrderUsers] = useState<userOption[]>([{ userID: 0, name: '', primaryContactNbr: '' }]);
    const [orderUser, setOrderUser] = useState<userResult[]>([]);
    const [dropdownValue, setDropdownValue] = useState<any>();
    const [resetKey, setResetKey] = useState(0);

    const complaintTypes = [
        { label: 'Internal', value: 1 },
        { label: 'Product', value: 2 },
        { label: 'Service', value: 3 }
    ];

    const [statuses, setStatuses] = useState<any[]>([]);
    const priorityOptions = [
        { label: 'Low', value: 'LOW' },
        { label: 'Medium', value: 'MEDIUM' },
        { label: 'High', value: 'HIGH' }
    ];

    useEffect(() => {
        loadStatuses();
    }, []);

    useEffect(() => {
        setOrderUser(orderUsers.map((user) => ({
            userID: user.userID,
            detail: user.userID !== 0 ? `${user.name}, ${user.primaryContactNbr}` : "",
        })));
    }, [orderUsers]);

    const loadStatuses = async () => {
        try {
            const statusList = await getComplaintStatus();
            const statusArray = Array.isArray(statusList) ? statusList : [];
            const mappedStatuses = statusArray.map((status: any) => ({
                label: String(status.status || status.name || 'Unknown'),
                value: String(status.complaintStatusId || status.id || status.statusId)
            }));

            setStatuses(mappedStatuses);
        } catch (error) {
            console.error('Error loading statuses:', error);
            setStatuses([]);
        }
    };

    const handleSearch = () => {
        const searchFilters: Partial<ComplaintSummaryRequest> = {
            complaintTypeId: filters.complaintTypeId || null,
            statusId: filters.statusId ? Number(filters.statusId) : null,
            priority: filters.priority || null,
            customerId: dropdownValue || null,
            createdOnFrom: filters.startDate ? `${filters.startDate.getFullYear()}-${String(filters.startDate.getMonth() + 1).padStart(2, '0')}-${String(filters.startDate.getDate()).padStart(2, '0')}` : null,
            createdOnTo: filters.endDate ? `${filters.endDate.getFullYear()}-${String(filters.endDate.getMonth() + 1).padStart(2, '0')}-${String(filters.endDate.getDate()).padStart(2, '0')}` : null
        };

        onSearch(searchFilters);
    };

    const handleReset = () => {
        setFilters({
            complaintTypeId: null,
            statusId: null,
            priority: null,
            startDate: null,
            endDate: null,
        });
        setOrderUsers([{ userID: 0, name: '', primaryContactNbr: '' }]);
        setDropdownValue(null);
        setResetKey(prev => prev + 1);

        onSearch({
            complaintTypeId: null,
            statusId: null,
            priority: null,
            customerId: null,
            createdOnFrom: null,
            createdOnTo: null,
            sortBy: 'critical',
            sortOrder: null
        });
    };

    return (
        <div className="complaint-search-container surface-card p-3 shadow-2 border-round-xl mt-3" style={{
            border: '1px solid rgba(76, 175, 80, 0.15)',
            background: '#ffffff'
        }}>
            <style>{`
                /* Search panel header */
                .complaint-search-container .search-panel-header {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-bottom: 1rem;
                    padding-bottom: 0.75rem;
                    border-bottom: 1px solid #f0f0f0;
                }

                .complaint-search-container .search-panel-header i {
                    color: #4CAF50;
                    font-size: 1rem;
                }

                .complaint-search-container .search-panel-header span {
                    font-weight: 600;
                    color: #374151;
                    font-size: 0.9rem;
                }

                /* Compact styling for single row - SCOPED TO SEARCH CONTAINER ONLY */
                .complaint-search-container .p-dropdown,
                .complaint-search-container .p-calendar {
                    height: 34px !important;
                    min-height: 34px !important;
                    font-size: 0.8125rem !important;
                    border-radius: 8px !important;
                    border-color: #e0e0e0 !important;
                    width: 100% !important;
                }

                .complaint-search-container .p-dropdown .p-dropdown-label {
                    padding: 0.4rem 0.75rem !important;
                    font-size: 0.8125rem !important;
                }

                .complaint-search-container .p-dropdown-trigger {
                    width: 2rem !important;
                }

                .complaint-search-container .p-calendar .p-inputtext {
                    padding: 0.4rem 0.75rem !important;
                    font-size: 0.8125rem !important;
                }

                .complaint-search-container .p-calendar .p-button {
                    width: 2rem !important;
                    padding: 0 !important;
                }

                .complaint-search-container .p-button {
                    height: 34px !important;
                    min-height: 34px !important;
                    padding: 0 0.75rem !important;
                    font-size: 0.8125rem !important;
                }

                .complaint-search-container .p-button-icon-only {
                    width: 34px !important;
                    padding: 0 !important;
                }

                /* Green hover effects - SCOPED */
                .complaint-search-container .p-dropdown:hover,
                .complaint-search-container .p-calendar:hover {
                    border-color: #4CAF50 !important;
                }

                .complaint-search-container .p-dropdown:focus-within,
                .complaint-search-container .p-calendar:focus-within {
                    border-color: #4CAF50 !important;
                    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.1) !important;
                }

                /* Dropdown Panel - SCOPED with unique class */
                .complaint-search-dropdown-panel.p-dropdown-panel {
                    border-radius: 12px !important;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
                    border: 1px solid #f0f0f0 !important;
                    padding: 0.5rem !important;
                }

                .complaint-search-dropdown-panel.p-dropdown-panel .p-dropdown-items {
                    padding: 0 !important;
                }

                .complaint-search-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item {
                    border-radius: 8px !important;
                    margin: 2px 0 !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.875rem !important;
                    transition: all 0.2s ease !important;
                }

                .complaint-search-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item:not(.p-highlight):not(.p-disabled):hover {
                    background: #4CAF50 !important;
                    color: #ffffff !important;
                }

                .complaint-search-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
                    background: #4CAF50 !important; /* Solid green selected */
                    color: #ffffff !important;
                    font-weight: 600 !important;
                }

                /* Button styling */
                .complaint-search-container .search-button-primary {
                    background: #4CAF50 !important;
                    border: none !important;
                    border-radius: 8px !important;
                }
                .complaint-search-container .search-button-primary:hover {
                    background: #45a049 !important;
                }

                .complaint-search-container .search-button-secondary {
                    background: transparent !important;
                    border: 1px solid #4CAF50 !important;
                    color: #4CAF50 !important;
                    border-radius: 8px !important;
                }
                .complaint-search-container .search-button-secondary:hover {
                    background: rgba(76, 175, 80, 0.05) !important;
                    color: #45a049 !important;
                    border-color: #45a049 !important;
                }

                /* Grid layout for single row */
                .complaint-search-container .compact-grid {
                    display: flex !important;
                    flex-wrap: wrap !important; /* Allow wrapping to prevent overlap */
                    gap: 1rem !important; /* Increased gap */
                    align-items: center !important;
                }

                .complaint-search-container .compact-grid > div {
                    flex: 0 0 auto !important;
                    margin-bottom: 0 !important;
                }

                /* Specific widths for each filter - Desktop */
                .complaint-search-container .type-filter { width: 120px !important; }
                .complaint-search-container .status-filter { width: 140px !important; }
                .complaint-search-container .priority-filter { width: 120px !important; }
                .complaint-search-container .date-filter { width: 140px !important; }
                .complaint-search-container .customer-filter { width: 180px !important; }
                .complaint-search-container .button-group { width: auto !important; }

                /* Mobile Responsiveness */
                @media screen and (max-width: 960px) {
                    .complaint-search-container .compact-grid {
                        gap: 0.5rem !important;
                    }
                    
                    .complaint-search-container .type-filter,
                    .complaint-search-container .status-filter,
                    .complaint-search-container .priority-filter,
                    .complaint-search-container .date-filter,
                    .complaint-search-container .customer-filter { 
                        width: calc(50% - 0.5rem) !important; 
                        flex: 1 1 auto !important;
                    }

                    .complaint-search-container .button-group {
                        width: 100% !important;
                        justify-content: flex-end;
                        margin-top: 0.5rem;
                    }
                }

                @media screen and (max-width: 576px) {
                    .complaint-search-container .type-filter,
                    .complaint-search-container .status-filter,
                    .complaint-search-container .priority-filter,
                    .complaint-search-container .date-filter,
                    .complaint-search-container .customer-filter { 
                        width: 100% !important; 
                    }
                }

                /* Compact Calendar Styles (Internal Definition) */
                .complaint-search-container .compact-calendar .p-inputtext {
                    padding: 0.4rem 0.5rem !important;
                    font-size: 0.8125rem !important;
                }
                .complaint-search-container .compact-calendar .p-button {
                    width: 2rem !important;
                }

                /* Calendar popup styling - SCOPED with unique class - COMPACT VERSION */
                .complaint-search-datepicker.p-datepicker {
                    min-width: 250px !important;
                    width: 250px !important; /* Fixed small width */
                    padding: 0.5rem !important;
                    font-size: 0.8rem !important; /* Smaller text */
                    border-radius: 12px !important;
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
                }

                .complaint-search-datepicker.p-datepicker .p-datepicker-header {
                    padding: 0.25rem !important;
                    margin-bottom: 0.25rem !important;
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                }

                .complaint-search-datepicker .p-datepicker-title,
                .complaint-search-datepicker .p-datepicker-title .p-datepicker-month,
                .complaint-search-datepicker .p-datepicker-title .p-datepicker-year {
                    font-size: 0.9rem !important; /* Smaller Header Text */
                    padding: 0.1rem !important;
                    margin: 0 !important;
                }

                .complaint-search-datepicker table th {
                    padding: 0.2rem !important;
                    font-size: 0.75rem !important;
                }

                .complaint-search-datepicker table td {
                    padding: 0 !important;
                }

                .complaint-search-datepicker table td > span {
                    width: 1.75rem !important;
                    height: 1.75rem !important;
                    font-size: 0.8rem !important;
                    margin: 1px auto !important; /* Tight Grid */
                }

                /* Hide labels to save space */
                .complaint-search-container .filter-label {
                    display: none !important;
                }
            `}</style>

            {/* Search Panel Header */}
            <div className="search-panel-header">
                <i className="pi pi-filter-fill"></i>
                <span>Filter Complaints</span>
            </div>

            {/* Single Row with all filters */}
            <div className="compact-grid">
                {/* Type Filter */}
                <div className="type-filter">
                    <label htmlFor="complaintType" className="filter-label">Type</label>
                    <Dropdown
                        id="complaintType"
                        value={filters.complaintTypeId}
                        options={complaintTypes}
                        onChange={(e) => setFilters(prev => ({ ...prev, complaintTypeId: e.value }))}
                        placeholder="Type"
                        className="w-full"
                        optionLabel="label"
                        optionValue="value"
                        showClear
                        panelClassName="complaint-search-dropdown-panel"
                    />
                </div>

                {/* Status Filter */}
                <div className="status-filter">
                    <label htmlFor="status" className="filter-label">Status</label>
                    <Dropdown
                        id="status"
                        value={filters.statusId}
                        options={statuses}
                        onChange={(e) => setFilters(prev => ({ ...prev, statusId: e.value }))}
                        placeholder="Status"
                        className="w-full"
                        optionLabel="label"
                        optionValue="value"
                        showClear
                        panelClassName="complaint-search-dropdown-panel"
                    />
                </div>

                {/* Priority Filter */}
                <div className="priority-filter">
                    <label htmlFor="priority" className="filter-label">Priority</label>
                    <Dropdown
                        id="priority"
                        value={filters.priority}
                        options={priorityOptions}
                        onChange={(e) => setFilters(prev => ({ ...prev, priority: e.value }))}
                        placeholder="Priority"
                        className="w-full"
                        optionLabel="label"
                        optionValue="value"
                        showClear
                        panelClassName="complaint-search-dropdown-panel"
                    />
                </div>

                {/* Start Date */}
                <div className="date-filter">
                    <label htmlFor="startDate" className="filter-label">From</label>
                    <Calendar
                        id="startDate"
                        key={`start-${resetKey}`}
                        value={filters.startDate}
                        onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.value as Date }))}
                        dateFormat="dd/mm/yy"
                        placeholder="Start Date"
                        className="w-full compact-calendar"
                        readOnlyInput
                        showIcon
                        icon="pi pi-calendar"
                        maxDate={filters.endDate || undefined}
                        panelClassName="complaint-search-datepicker"
                    />
                </div>

                {/* End Date */}
                <div className="date-filter">
                    <label htmlFor="endDate" className="filter-label">To</label>
                    <Calendar
                        id="endDate"
                        key={`end-${resetKey}`}
                        value={filters.endDate}
                        onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.value as Date }))}
                        dateFormat="dd/mm/yy"
                        placeholder="End Date"
                        className="w-full compact-calendar"
                        readOnlyInput
                        showIcon
                        icon="pi pi-calendar"
                        minDate={filters.startDate || undefined}
                        panelClassName="complaint-search-datepicker"
                    />
                </div>

                {/* Customer Filter */}
                <div className="customer-filter">
                    <label htmlFor="userSearch" className="filter-label">Customer</label>
                    <Dropdown
                        id="userSearch"
                        options={orderUser || []}
                        optionLabel="detail"
                        optionValue="userID"
                        value={dropdownValue}
                        onChange={(e) => setDropdownValue(e.value)}
                        onFilter={(e: DropdownFilterEvent) => {
                            if (e.filter.length > 2) {
                                orderServices.getOrderUsers(e.filter).then((response: userOption[]) => {
                                    if (response.length > 0) {
                                        setOrderUsers(response);
                                    }
                                });
                            }
                        }}
                        filter
                        placeholder="Customer..."
                        className="w-full"
                        showClear
                        panelClassName="complaint-search-dropdown-panel"
                    />
                </div>

                {/* Buttons */}
                <div className="button-group flex gap-2">
                    <Button
                        icon="pi pi-filter"
                        onClick={handleSearch}
                        className="search-button-primary"
                        tooltip="Search"
                        tooltipOptions={{ position: 'top' }}
                    />
                    <Button
                        icon="pi pi-refresh"
                        onClick={handleReset}
                        className="search-button-secondary"
                        tooltip="Reset"
                        tooltipOptions={{ position: 'top' }}
                    />
                    <FileActionButton action="export"
                        icon="pi pi-file-excel"
                        label="Export"
                        onClick={onExport}
                        className="search-button-primary"
                        tooltip="Export"
                        tooltipOptions={{ position: 'top' }}
                    />
                </div>
            </div>
        </div>
    );
};

export default ComplaintSummarySearch;
